import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../components/form/input/InputField";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { DatePicker } from "../../components/common/DatePicker";
import { CheckCircle } from "lucide-react";
import ListboxComponent from "../../components/common/ListboxComponent";
import {
  modalStyles,
  modalTitle,
  modalDescription,
  formStyles,
  label,
  input,
  errorMessage,
  closeButton,
  saveButton,
  saveButtonDisabled,
  successModalStyles,
  successModalCloseButton,
  successModalIconContainer,
  successModalHeading,
  successModalButton,
} from "../../styles/components/UserProfile/UserInfoCard.styles";

type TaskModalProps = {
  isOpen: boolean;
  onClose: () => void;
  state: {
    lead_id?: string;
  };
  onTaskAdded?: () => void;
};

const formatDate = (date: string | Date | number): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  state,
  onTaskAdded,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    task_name: "",
    task_description: "",
    assigned_to: "",
    due_date: "",
    duration: "",
    start_datetime: "",
    status_id: "",
  });
  const { lead_id } = state;
  const [saving, setSaving] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [errors, setErrors] = useState<{
    task_name?: string;
    task_description?: string;
    due_date?: string;
    duration?: string;
    start_datetime?: string;
    assigned_to?: string;
    status_id?: string;
  }>({});
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<
    { id: string; status_name: string }[]
  >([]);
  const [, setIsLoadingUsers] = useState(false);
  const [, setIsLoadingStatuses] = useState(false);

  const token = sessionStorage.getItem("access_token");
  const userRoles = JSON.parse(sessionStorage.getItem("user_roles") || "[]");
  const isAdmin = userRoles.includes("Admin");

  const assignedToOptions = users.map((u) => ({ id: u.id, name: u.name }));
  const statusOptions = leadStatuses.map((s) => ({
    id: s.id,
    name: s.status_name,
  }));

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/name/list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }

        const data = await response.json();
        if (data.status === 200) {
          setUsers(data.data);
        } else {
          setErrors((prev) => ({ ...prev, assigned_to: "Failed to load users." }));
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setErrors((prev) => ({ ...prev, assigned_to: "Error loading users." }));
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (token) {
      fetchUsers();
    }
  }, [token]);

  useEffect(() => {
    const fetchLeadStatuses = async () => {
      setIsLoadingStatuses(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/status/name/list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch lead statuses");
        }

        const result = await response.json();
        if (result.status === 200) {
          setLeadStatuses(result.data);
        } else {
          setErrors((prev) => ({
            ...prev,
            status_id: "Failed to load statuses.",
          }));
        }
      } catch (error) {
        console.error("Error fetching lead statuses:", error);
        setErrors((prev) => ({ ...prev, status_id: "Error loading statuses." }));
      } finally {
        setIsLoadingStatuses(false);
      }
    };

    if (token) {
      fetchLeadStatuses();
    }
  }, [token]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof formData
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCancel = () => {
    setFormData({
      task_name: "",
      task_description: "",
      assigned_to: "",
      due_date: "",
      duration: "",
      start_datetime: "",
      status_id: "",
    });
    setErrors({});
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const newErrors: typeof errors = {};
    if (!formData.task_name.trim()) newErrors.task_name = "Task name field is required.";
    if (!formData.task_description.trim())
      newErrors.task_description = "Task description field is required.";
    if (!formData.due_date) newErrors.due_date = "Due date field is required.";
    if (!formData.duration.trim()) {
      newErrors.duration = "Duration is required.";
    } else if (isNaN(Number(formData.duration)) || Number(formData.duration) <= 0) {
      newErrors.duration = "Duration field must be a positive number.";
    }
    if (!formData.start_datetime) newErrors.start_datetime = "Start date field is required.";
    if (!formData.status_id) newErrors.status_id = "Status field is required.";

    if (formData.due_date && formData.start_datetime) {
      const start = new Date(formData.start_datetime.split("-").reverse().join("-"));
      const due = new Date(formData.due_date.split("-").reverse().join("-"));
      if (due < start) {
        newErrors.due_date = "Due date must be after start date.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    if (!token) {
      setErrors((prev) => ({
        ...prev,
        assigned_to: "Authorization required. Please log in.",
      }));
      navigate("/signin");
      setSaving(false);
      return;
    }

    try {
      const payload = {
        task_name: formData.task_name,
        task_description: formData.task_description,
        assigned_to: formData.assigned_to,
        due_date: formData.due_date,
        duration: formData.duration,
        start_datetime: formData.start_datetime,
        lead_id: lead_id ?? "",
        status_id: formData.status_id,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/task/add`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setFormData({
          task_name: "",
          task_description: "",
          assigned_to: "",
          due_date: "",
          duration: "",
          start_datetime: "",
          status_id: "",
        });
        onClose();
        setTimeout(() => {
          setSuccessModalOpen(false);
          if (onTaskAdded) onTaskAdded();
        }, 2000);
      } else {
        setErrors((prev) => ({
          ...prev,
          assigned_to: response.data.message || "Failed to save task.",
        }));
      }
    } catch (error: any) {
      console.error("API Error:", error.response?.data || error.message);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrors((prev) => ({
          ...prev,
          assigned_to: "Session expired. Please log in.",
        }));
        navigate("/signin");
      } else {
        setErrors((prev) => ({
          ...prev,
          assigned_to:
            error.response?.data?.message || "Something went wrong. Please try again.",
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCancel} className={modalStyles}>
        <h4 className={modalTitle}>Add Lead Task</h4>
        <p className={modalDescription}>
          Fill in the details to create a new task.
        </p>
        <form onSubmit={handleSubmit} className={formStyles}>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-1">
              <label className={label}>
                Task Name <span className="text-error-500 dark:text-red-400">*</span>
              </label>
              <Input
                type="text"
                value={formData.task_name}
                onChange={(e) => handleChange(e, "task_name")}
                className={`${input} ${
                  errors.task_name
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
              {errors.task_name && <p className={errorMessage}>{errors.task_name}</p>}
            </div>
            <div className="flex-1">
              <label className={label}>
                Duration (in minutes) <span className="text-error-500 dark:text-red-400">*</span>
              </label>
              <Input
                type="text"
                min="1"
                value={formData.duration}
                numericOnly={true}
                onChange={(e) => handleChange(e, "duration")}
                className={`${input} ${
                  errors.duration
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
              {errors.duration && <p className={errorMessage}>{errors.duration}</p>}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-1">
              <DatePicker
                value={formData.start_datetime}
                onChange={(dates) => {
                  const date = dates[0];
                  if (date) {
                    const formatted = formatDate(date);
                    setFormData((prev) => ({ ...prev, start_datetime: formatted }));
                    setErrors((prev) => ({ ...prev, start_datetime: undefined }));
                  } else {
                    setFormData((prev) => ({ ...prev, start_datetime: "" }));
                    setErrors((prev) => ({ ...prev, start_datetime: undefined }));
                  }
                }}
                label="Start Date"
                required
                error={errors.start_datetime}
                className={`${input} ${
                  errors.start_datetime
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
            </div>
            <div className="flex-1">
              <DatePicker
                value={formData.due_date}
                onChange={(dates) => {
                  const date = dates[0];
                  if (date) {
                    const formatted = formatDate(date);
                    setFormData((prev) => ({ ...prev, due_date: formatted }));
                    setErrors((prev) => ({ ...prev, due_date: undefined }));
                  } else {
                    setFormData((prev) => ({ ...prev, due_date: "" }));
                    setErrors((prev) => ({ ...prev, due_date: undefined }));
                  }
                }}
                label="Due Date"
                required
                error={errors.due_date}
                className={`${input} ${
                  errors.due_date
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            {isAdmin && (
              <div className="flex-1">
                <ListboxComponent
                  value={assignedToOptions.find((opt) => opt.id === formData.assigned_to) || null}
                  onChange={(selected) => {
                    const val = selected?.id || "";
                    setFormData((prev) => ({ ...prev, assigned_to: val }));
                  }}
                  items={assignedToOptions}
                  label="Assigned To"
                  displayKey="name"
                  className={`${input} ${
                    errors.assigned_to
                      ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                      : ""
                  }`}
                />
                {errors.assigned_to && (
                  <p className={errorMessage}>{errors.assigned_to}</p>
                )}
              </div>
            )}
            <div className={isAdmin ? "flex-1" : "w-full"}>
              <ListboxComponent
                value={statusOptions.find((opt) => opt.id === formData.status_id) || null}
                onChange={(selected) => {
                  const val = selected?.id || "";
                  setFormData((prev) => ({ ...prev, status_id: val }));
                  setErrors((prev) => ({
                    ...prev,
                    status_id: val ? undefined : "Status field is required.",
                  }));
                }}
                items={statusOptions}
                label="Status"
                displayKey="name"
                required
                error={errors.status_id}
                className={`${input} ${
                  errors.status_id
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                  }`}
              />
            </div>
          </div>
          <div className="relative">
            <label className={label}>
              Task Description <span className="text-error-500 dark:text-red-400">*</span>
            </label>
            <textarea
              value={formData.task_description}
              onChange={(e) => handleChange(e, "task_description")}
              rows={4}
              className={`${input} min-h-[5rem] ${
                errors.task_description
                  ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                  : ""
              }`}
            />
            {errors.task_description && (
              <p className={errorMessage}>{errors.task_description}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 items-center">
            <div className="flex-1 flex gap-4 justify-center sm:justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`${saveButton} ${saving ? saveButtonDisabled : ""}`}
              >
                {saving ? "Saving..." : "Submit"}
              </button>
              <button type="button" onClick={handleCancel} className={closeButton}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className={successModalStyles}
      >
        <button
          onClick={() => setSuccessModalOpen(false)}
          className={successModalCloseButton}
        />
        <div className="flex justify-center">
          <div className={successModalIconContainer}>
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className={successModalHeading}>Task Created Successfully</h4>
        <p className="text-sm text-gray-500 dark:text-white/90 mt-2">
          The task has been added to the system successfully.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className={successModalButton}
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default TaskModal;